import React, { useEffect, useMemo, useState } from "react";
import { fetchLeagueTrades } from "@/lib/api";
import { fmtNum, positionColor } from "@/lib/format";
import { playerBlendKey } from "@/lib/adminRankings";
import { SignInRequired } from "@/components/SignInRequired";
import { Trophy, Scale, ArrowRight } from "lucide-react";

export function LeagueTrades({ embedded = false, isGuest = false, onSignIn, leagueId, leagueName, players = [] }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) { setTrades([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const d = await fetchLeagueTrades(leagueId);
        setTrades(d.trades || []);
      } catch (e) { setTrades([]); }
      finally { setLoading(false); }
    })();
  }, [leagueId]);

  const valuedTrades = useMemo(() => {
    const byKey = new Map();
    const byName = new Map();
    for (const player of players) {
      byKey.set(playerBlendKey(player), player);
      if (player.name) byName.set(player.name, player);
    }

    return trades.map((trade) => {
      const sides = (trade.sides || []).map((side) => {
        const assets = (side.assets || []).map((asset) => {
          const match = byKey.get(playerBlendKey(asset)) || byName.get(asset.name);
          return match ? { ...asset, combinedValue: match.combinedValue, combinedRank: match.combinedRank } : asset;
        });
        return {
          ...side,
          assets,
          total_value: assets.reduce((sum, asset) => sum + (Number(asset.combinedValue) || 0), 0),
        };
      });
      const sortedSides = [...sides].sort((a, b) => (b.total_value || 0) - (a.total_value || 0));
      const top = sortedSides[0]?.total_value || 0;
      const bottom = sortedSides[sortedSides.length - 1]?.total_value || 0;
      const diff = top - bottom;
      const diff_pct = top ? Number(((diff / top) * 100).toFixed(1)) : 0;
      return {
        ...trade,
        sides,
        diff,
        diff_pct,
        winner_roster_id: diff_pct < 5 ? null : sortedSides[0]?.roster_id,
      };
    });
  }, [players, trades]);

  if (isGuest) {
    return (
      <section className={embedded ? "mt-12" : "mt-16"} data-testid="trades-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">04 / Trade History</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">League trades.</h2>
            <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
              Every completed trade in your league, valued in TRYSTOMATIC and graded by who actually won the deal.
            </p>
          </div>
        </div>
        <SignInRequired
          description="Sign in with your Sleeper username to browse trade history from your leagues."
          onSignIn={onSignIn}
          testId="guest-trades-cta"
        />
      </section>
    );
  }

  if (!leagueId) {
    return (
      <section className={embedded ? "mt-12" : "mt-16"} data-testid="trades-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">04 / Trade History</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">League trades.</h2>
            <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
              Every completed trade in your league, valued in TRYSTOMATIC and graded by who actually won the deal.
            </p>
          </div>
        </div>
        <div className="brutal-card p-6 text-sm text-[#52525B]" data-testid="trades-no-league">
          Select a league above to view trade history.
        </div>
      </section>
    );
  }

  return (
    <section className={embedded ? "mt-12" : "mt-16"} data-testid="trades-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">04 / Trade History</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">League trades.</h2>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            Every completed trade in <span className="font-mono-data text-[#0A0A0A]">{leagueName}</span>, valued in TRYSTOMATIC and graded by who actually won the deal.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#52525B] font-mono-data py-8">Loading trade history…</div>
      ) : valuedTrades.length === 0 ? (
        <div className="brutal-card p-6 text-sm text-[#52525B]" data-testid="no-trades">
          No completed trades found for this league yet.
        </div>
      ) : (
        <div className="space-y-4">
          {valuedTrades.map((t) => <TradeCard key={t.transaction_id} trade={t} />)}
        </div>
      )}
    </section>
  );
}

export function TradeCard({ trade }) {
  const date = trade.created ? new Date(trade.created) : null;
  const isFair = trade.verdict === "FAIR";
  const verdictBg = isFair ? "#FACC15" : "#00C853";

  return (
    <div className="brutal-card" data-testid={`trade-${trade.transaction_id}`}>
      <div className="border-b border-[#0A0A0A] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="label-eyebrow">{date ? date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "Date —"}</div>
          {trade.week != null && trade.week > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data border border-[#0A0A0A]/20 px-1.5 py-0.5">Week {trade.week}</span>
          )}
        </div>
        <div className="flex items-center gap-2 px-2.5 py-1 border border-[#0A0A0A]" style={{ background: verdictBg }}>
          {isFair ? <Scale size={12} /> : <Trophy size={12} />}
          <span className="font-display font-bold text-xs tracking-tight uppercase">{trade.verdict}</span>
          <span className="font-mono-data text-[10px] tabular-nums">· Δ {fmtNum(trade.diff)} ({trade.diff_pct}%)</span>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${trade.sides.length === 2 ? "md:grid-cols-2" : "md:grid-cols-" + trade.sides.length} divide-y md:divide-y-0 md:divide-x divide-[#0A0A0A]/10`}>
        {trade.sides.map((side, idx) => {
          const isWinner = trade.winner_roster_id === side.roster_id;
          return (
            <div key={side.roster_id} className="p-4" data-testid={`trade-side-${side.roster_id}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-base truncate">{side.team_name}</span>
                    {isWinner && <Trophy size={14} className="text-[#00C853]" />}
                  </div>
                  <div className="text-[11px] text-[#52525B] font-mono-data truncate">received {side.assets.length} {side.assets.length === 1 ? "asset" : "assets"}</div>
                </div>
                <div className="text-right">
                  <div className="label-eyebrow">Got</div>
                  <div className="font-mono-data font-bold tabular-nums text-lg">{fmtNum(side.total_value)}</div>
                </div>
              </div>
              <div className="space-y-1">
                {side.assets.map((a, i) => (
                  <div key={`${a.id}-${i}`} className="flex items-center justify-between gap-2 px-2 py-1.5 border border-[#0A0A0A]/10 text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(a.position)}`}>{a.position}</span>
                      <span className="truncate">{a.name}</span>
                    </div>
                    <span className="font-mono-data text-xs tabular-nums text-[#52525B]">{fmtNum(a.combinedValue)}</span>
                  </div>
                ))}
                {side.assets.length === 0 && (
                  <div className="text-xs text-[#52525B] py-3 text-center border border-dashed border-[#0A0A0A]/30">Nothing received.</div>
                )}
              </div>
              {idx < trade.sides.length - 1 && (
                <div className="hidden md:flex absolute"><ArrowRight size={16} /></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
